import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { CATEGORIES, PRICE_UNITS, EMPTY_ITEM } from './catalogConstants';

const SERVICE_ONLY = ['category', 'buffer_minutes', 'direct_booking', 'option_codes', 'tax_credit_eligible', 'show_tax_badge'];
const selectCls = 'w-full h-10 rounded-md border border-input bg-background px-3 text-sm';

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const ToggleRow = ({ label, hint, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-3">
    <div>
      <p className="text-sm font-semibold text-slate-700">{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
  </div>
);

const toPayload = (form, isService, isNew) => {
  const out = { ...form };
  out.price = out.pricing_mode === 'fixed' && out.price !== '' ? Number(out.price) : null;
  out.duration_minutes = out.duration_minutes === '' ? null : Number(out.duration_minutes);
  out.buffer_minutes = Number(out.buffer_minutes || 0);
  out.display_order = Number(out.display_order || 0);
  if (!isService) SERVICE_ONLY.forEach((k) => delete out[k]);
  if (!isNew) delete out.code;
  ['id', 'created_at', 'updated_at', 'icon'].forEach((k) => delete out[k]);
  return out;
};

export const CatalogItemDialog = ({ open, item, isService, options, onClose, onSave }) => {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const isNew = !item?.id;

  useEffect(() => {
    if (open) setForm({ ...EMPTY_ITEM, ...item, price: item?.price ?? '', duration_minutes: item?.duration_minutes ?? '' });
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canDirect = form.pricing_mode === 'fixed' && Number(form.price) > 0 && Number(form.duration_minutes) > 0;
  useEffect(() => {
    if (!canDirect && form.direct_booking) setForm((f) => ({ ...f, direct_booking: false }));
  }, [canDirect, form.direct_booking]);
  const toggleOption = (code) =>
    set('option_codes', form.option_codes.includes(code)
      ? form.option_codes.filter((c) => c !== code)
      : [...form.option_codes, code]);

  const submit = async () => {
    setSaving(true);
    try { await onSave(toPayload(form, isService, isNew)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-item-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Nouvelle' : 'Modifier la'} {isService ? 'prestation' : 'option'}</DialogTitle>
          <DialogDescription>Les modifications ne changent pas le site public pour le moment.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nom" >
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="input-name" />
          </Field>
          <Field label="Code (fixe)" hint={isNew ? 'Minuscules, chiffres et _ uniquement. Non modifiable ensuite.' : 'Le code ne peut pas être modifié.'}>
            <Input value={form.code} disabled={!isNew} onChange={(e) => set('code', e.target.value)} data-testid="input-code" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description">
              <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="input-description" />
            </Field>
          </div>
          {isService && (
            <Field label="Catégorie">
              <select className={selectCls} value={form.category} onChange={(e) => set('category', e.target.value)} data-testid="select-category">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
          )}
          <Field label="Type de prix">
            <select className={selectCls} value={form.pricing_mode} onChange={(e) => set('pricing_mode', e.target.value)} data-testid="select-pricing-mode">
              <option value="quote">Sur devis</option>
              <option value="fixed">Prix fixe</option>
            </select>
          </Field>
          {form.pricing_mode === 'fixed' && (
            <>
              <Field label="Prix (€ TTC)">
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="input-price" />
              </Field>
              <Field label="Unité">
                <select className={selectCls} value={form.price_unit} onChange={(e) => set('price_unit', e.target.value)} data-testid="select-price-unit">
                  {PRICE_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </Field>
            </>
          )}
          <Field label="Durée estimée (minutes)">
            <Input type="number" min="0" value={form.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} data-testid="input-duration" />
          </Field>
          {isService && (
            <Field label="Temps de battement (minutes)" hint="Trajet / rangement entre deux interventions.">
              <Input type="number" min="0" value={form.buffer_minutes} onChange={(e) => set('buffer_minutes', e.target.value)} data-testid="input-buffer" />
            </Field>
          )}
          <Field label="Ordre d'affichage">
            <Input type="number" value={form.display_order} onChange={(e) => set('display_order', e.target.value)} data-testid="input-display-order" />
          </Field>
        </div>

        {isService && options.length > 0 && (
          <Field label="Options complémentaires proposées">
            <div className="grid sm:grid-cols-2 gap-2">
              {options.map((o) => (
                <label key={o.code} className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
                  <Checkbox checked={form.option_codes.includes(o.code)} onCheckedChange={() => toggleOption(o.code)} data-testid={`checkbox-option-${o.code}`} />
                  {o.name}{!o.active && <span className="text-xs text-slate-400">(inactive)</span>}
                </label>
              ))}
            </div>
          </Field>
        )}

        <div className="space-y-2">
          {isService && (
            <ToggleRow
              label="Réservation directe"
              hint={canDirect ? 'Le client pourra réserver un créneau (étape calendrier, à venir).' : 'Nécessite un prix fixe et une durée.'}
              checked={form.direct_booking}
              disabled={!canDirect}
              onChange={(v) => set('direct_booking', v)}
              testid="switch-direct-booking"
            />
          )}
          {isService && (
            <ToggleRow label="Éligible au crédit d'impôt 50 %" checked={form.tax_credit_eligible}
              onChange={(v) => set('tax_credit_eligible', v)} testid="switch-tax-credit" />
          )}
          {isService && (
            <ToggleRow label="Afficher le badge « -50 % d'impôts » sur le site" hint="Affichage uniquement : ne change pas l'éligibilité."
              checked={form.show_tax_badge} onChange={(v) => set('show_tax_badge', v)} testid="switch-show-tax-badge" />
          )}
          <ToggleRow label="Active" hint="Une prestation inactive est conservée mais masquée." checked={form.active}
            onChange={(v) => set('active', v)} testid="switch-active" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="catalog-cancel-button">Annuler</Button>
          <Button onClick={submit} disabled={saving || !form.name || (isNew && !form.code)} className="gap-1.5" data-testid="catalog-save-button">
            <Save className="w-4 h-4" /> Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
